<template>
  <div class="medium-12 columns">
    <div v-if="getTemplateHeader">
      <label>{{ $t('WHATSAPP_TEMPLATES.PARSER.HEADER') }}</label>
      <file-upload
        v-if="formatTypeTemplateHeader !== 'TEXT' && !hasAttachments"
        ref="images"
        name="_file"
        :accept="acceptedFormats"
        :size="acceptedSize"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <woot-button
          class-names="button--upload"
          icon="file-upload"
          color-scheme="secondary"
          variant="smooth"
          size="small"
        >
          <span v-if="formatTypeTemplateHeader === 'IMAGE'">
            {{ $t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_IMAGE') }}
          </span>

          <span v-if="formatTypeTemplateHeader === 'DOCUMENT'">
            {{ $t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_DOCUMENT') }}
          </span>

          <span v-if="formatTypeTemplateHeader === 'VIDEO'">
            {{ $t('WHATSAPP_TEMPLATES.PARSER.UPLOAD_VIDEO') }}
          </span>
        </woot-button>

        <small>
          Formatos permitidos: <span>{{ acceptedFormats }}</span>
        </small>
      </file-upload>
    </div>

    <div v-if="hasAttachments" class="attachment-preview-box">
      <attachment-preview
        :attachments="attachedFiles"
        :remove-attachment="removeAttachment"
      />
    </div>
    <div class="bg-image">
      <div class="card">
        <div v-if="formatTypeTemplateHeader === 'TEXT' && processedStringHeader" class="header">
          <p>{{ processedStringHeader }}</p>
        </div>

        <div class="body">
          <p>{{ processedStringBody }}</p>
        </div>

        <div v-if="processedStringFooter" class="footer">
          <p>{{ processedStringFooter }}</p>
        </div>

        <div v-if="getTemplateButtons.length" class="template-buttons">
          <div class="template-button" v-for="(button, index) in getTemplateButtons" :key="`button-${index}`">
            {{ button.text }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="variablesHeader" class="template__variables-container">
      <p class="variables-label">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_HEADER') }}
      </p>
      <div
        v-for="(variable, key) in processedParamsHeader"
        :key="key"
        class="template__variable-item"
      >
        <span class="variable-label">
          {{ key }}
        </span>
        <woot-input
          v-model="processedParamsHeader[key]"
          type="text"
          class="variable-input"
          :styles="{ marginBottom: 0 }"
          :readonly="
            canDisable(key, processedParamsHeader[key], wootConstants.HEADER)
          "
          @blur="sanitizeVariable(processedParamsHeader, key)"
          @paste.native="onPasteVariable($event, processedParamsHeader, key)"
        />
      </div>
    </div>

    <div v-if="variablesBody" class="template__variables-container">
      <p class="variables-label">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_BODY') }}
      </p>
      <div
        v-for="(variable, key) in processedParamsBody"
        :key="key"
        class="template__variable-item"
      >
        <span class="variable-label">
          {{ key }}
        </span>
        <woot-input
          v-model="processedParamsBody[key]"
          type="text"
          class="variable-input"
          :styles="{ marginBottom: 0 }"
          :readonly="
            canDisable(key, processedParamsBody[key], wootConstants.BODY)
          "
          @blur="
            sanitizeVariable(processedParamsBody, key);
            onBlur(processedParamsBody);
          "
          @paste.native="onPasteVariable($event, processedParamsBody, key)"
        />
      </div>
      <p v-if="$v.$dirty && $v.$invalid" class="error">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <div v-if="hasButtonVariables" class="template__variables-container">
      <p class="variables-label">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_BUTTON') }}
      </p>
      <template v-for="(variables, index) in processedParamsButtons">
        <div
          v-for="(variable, key) in variables"
          :key="`button-${index}-${key}`"
          class="template__variable-item template__variable-item--button"
        >
          <div class="variable-button-row">
            <span class="variable-label">{{ getButtonByIndex(index).text }}</span>
            <woot-input
              v-model="processedParamsButtons[index][key]"
              type="text"
              class="variable-input"
              :styles="{ marginBottom: 0 }"
              @blur="sanitizeVariable(processedParamsButtons[index], key)"
              @paste.native="
                onPasteVariable($event, processedParamsButtons[index], key)
              "
            />
          </div>
          <span class="variable-url">{{ processedButtonUrl(index) }}</span>
          <p v-if="isButtonUrlInvalid(index)" class="warning variable-url-warning">
            {{ $t('WHATSAPP_TEMPLATES.PARSER.INVALID_BUTTON_URL') }}
          </p>
        </div>
      </template>
    </div>
    <div v-if="isLowQualityNumber" class="card-error">
      <fluent-icon icon="warning" size="60" />
      <p
        v-if="isTemplateSendBlocked"
        v-dompurify-html="$t('WHATSAPP_TEMPLATES.PARSER.LOW_QUALITY_NUMBER_BLOCKED')"
      />
      <p
        v-else
        v-dompurify-html="$t('WHATSAPP_TEMPLATES.PARSER.LOW_QUALITY_NUMBER_WARNING')"
      />
    </div>
    <p v-if="showAttachmentWarning && isAttachmentRequired" class="warning">
      {{ $t('WHATSAPP_TEMPLATES.PARSER.MISSING_ATTACHMENT') }}
    </p>
    <footer :class="{ 'custom-footer': type === 'campaign' }">
      <woot-button
        v-if="type === 'default'"
        variant="smooth"
        :is-disabled="submitting"
        @click="$emit('resetTemplate')"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL') }}
      </woot-button>

      <woot-button
        v-if="type !== 'webhook_send_message'"
        :is-loading="submitting"
        :is-disabled="isTemplateSendBlocked"
        @click="sendMessage"
      >
        {{
          $t(
            type === 'campaign'
              ? 'CAMPAIGN.NEXT.BUTTON_TEXT'
              : 'WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL'
          )
        }}
      </woot-button>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import FileUpload from 'vue-upload-component';
import alertMixin from 'shared/mixins/alertMixin';
import parameterizationMixin from 'shared/mixins/parameterizationMixin';
import templateWppMixin from 'shared/mixins/templateWppMixin';
import inboxMixin from 'shared/mixins/inboxMixin';
import {
  insertSanitizedPaste,
  sanitizeTemplateParam,
} from 'dashboard/helper/templateParamsHelper';

export default {
  components: { AttachmentPreview, FileUpload },
  mixins: [alertMixin, parameterizationMixin, templateWppMixin, inboxMixin],
  props: {
    template: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      default: 'default',
    },
    enableParameterization: {
      type: Boolean,
      default: false,
    },
    valueProcessedParams: {
      type: Object,
      default: () => {},
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
  },
  data() {
    return {
      showAttachmentWarning: false,
      templateSendStatus: null,
    };
  },
  watch: {
    inboxId() {
      this.fetchQualityNumber();
    },
  },
  mounted() {
    let payload = this.processPayload();
    this.$emit('initTemplate', payload);
    this.fetchQualityNumber();
  },
  computed: {
    ...mapGetters({
      conversationsUiFlags: 'contactConversations/getUIFlags',
      messageUiFlags: 'getMessageUIFlags',
      getInbox: 'inboxes/getInbox',
    }),
    inbox() {
      return this.getInbox(this.inboxId) || {};
    },
    isAttachmentRequired() {
      return ['IMAGE', 'DOCUMENT', 'VIDEO'].includes(
        this.formatTypeTemplateHeader
      );
    },
    hasParameters() {
      return this.enableParameterization && this.parameterization?.parameterizations.length > 0;
    },
    submitting() {
      return this.messageUiFlags.isCreating || this.conversationsUiFlags.isCreating;
    },
    isLowQualityNumber() {
      return this.templateSendStatus?.quality_rating === 'low';
    },
    isTemplateSendBlocked() {
      return !!this.templateSendStatus?.templates_blocked;
    },
  },
  methods: {
    sendMessage() {
      if (this.isTemplateSendBlocked) return;

      this.$v.$touch();

      const needsAttachment = this.isAttachmentRequired && !this.hasAttachments;

      if (needsAttachment) {
        this.showAttachmentWarning = true;
        return;
      }

      if (this.$v.$invalid) return;

      this.showAttachmentWarning = false;

      let payload = this.processPayload();

      if (this.hasAnAttach()) {
        const processed_params = this.getFileProcessedParameters();
        payload.templateParams.file_processed_parameters = processed_params;
      }

      this.$emit('sendMessage', payload);
      this.sendAttachmentPrivateMessage();
    },
    onPasteVariable(event, parameters, key) {
      const input = event.target;
      const clipboard = event.clipboardData;
      if (!clipboard || input.readOnly) return;

      event.preventDefault();
      const { value, cursor } = insertSanitizedPaste(
        input,
        clipboard.getData('text')
      );
      this.$set(parameters, key, value);
      this.$nextTick(() => input.setSelectionRange(cursor, cursor));
    },
    sanitizeVariable(parameters, key) {
      const sanitized = sanitizeTemplateParam(parameters[key]);
      if (sanitized !== parameters[key]) this.$set(parameters, key, sanitized);
    },
    onBlur(parameters) {
      let payload = this.processPayload();
      this.$emit('onBlur', {parameters, ...payload});
    },
    processPayload() {
      let message = null;
      let processed_params = null;

      if (this.enableParameterization && this.parameterization) {
        if (this.type === 'campaign' || this.type === 'contact' || this.type === 'chat') {
          message = this.getMessage();
          processed_params = this.getProcessedParams();
        } else {
          message = this.getParameterizedMessages();
          processed_params = this.getProcessedParamsParameterized();
        }
      } else {
        message = this.getMessage();
        processed_params = this.getProcessedParams();
      }

      return {
        message,
        templateParams: {
          name: this.template.name,
          category: this.template.category,
          language: this.template.language,
          namespace: this.template.namespace,
          processed_params,
        },
        hasParameters: this.hasParameters,
      };
    },
    sendAttachmentPrivateMessage() {
      if (!this.hasAnAttach()) return;

      const file = this.attachedFiles[0];
      file.isPrivate = true;

      const payload = {
        message: '',
        private: true,
        files: [file.blobSignedId],
        attachedFile: this.resource,
      };

      this.$emit('sendMessage', payload);
    },
    async fetchQualityNumber() {
      const requestInboxId = this.inboxId;
      this.templateSendStatus = null;
      if (!this.is360DialogCloudWhatsAppChannel) return;

      try {
        const data = await this.$store.dispatch('inboxes/getTemplateSendStatus', {
          inboxId: requestInboxId,
        });
        if (requestInboxId !== this.inboxId) return;

        this.templateSendStatus = data || null;
      } catch (error) {
        if (requestInboxId !== this.inboxId) return;
        this.templateSendStatus = null;
        console.error('[WhatsappQualityNumber] failed to fetch template send status', error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.template__variables-container {
  padding: var(--space-one);
}

.variables-label {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-one);
}

.template__variable-item {
  align-items: center;
  display: flex;
  margin-bottom: var(--space-one);

  .label {
    font-size: var(--font-size-mini);
  }

  .variable-input {
    flex: 1;
    font-size: var(--font-size-small);
    margin-left: var(--space-one);
  }

  .variable-label {
    background-color: var(--s-75);
    border-radius: var(--border-radius-normal);
    display: inline-block;
    font-size: var(--font-size-mini);
    padding: var(--space-one) var(--space-medium);
  }

  &--button {
    align-items: stretch;
    flex-direction: column;

    .variable-button-row {
      align-items: center;
      display: flex;
    }

    .variable-url {
      color: var(--s-500);
      font-size: var(--font-size-mini);
      margin-top: var(--space-smaller);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .variable-url-warning {
      margin-top: var(--space-smaller);
      margin-bottom: 0;
      padding: var(--space-smaller) var(--space-small);
      text-align: left;
    }
  }
}

footer {
  display: flex;
  justify-content: flex-end;

  &.custom-footer {
    position: absolute;
    right: var(--space-large);
    margin-top: var(--space-small);
  }

  button {
    margin-left: var(--space-one);
  }
}
.error {
  background-color: var(--r-100);
  border-radius: var(--border-radius-normal);
  color: var(--r-800);
  padding: var(--space-one);
  text-align: center;
}
.warning {
  background-color: var(--y-100);
  border-radius: var(--border-radius-normal);
  color: var(--y-800);
  padding: var(--space-one);
  margin-bottom: var(--space-one);
  text-align: center;
}
.template-input {
  background-color: var(--s-25);
}
.card-error {
  background-color: var(--r-100);
  border-radius: var(--border-radius-normal);
  color: var(--r-800);
  padding: var(--space-one);
  display: flex;
  align-items: center;
  gap: var(--space-normal);
  margin: var(--space-normal) 0;
}

.preview-item__wrap,
.file-uploads-html5 {
  margin-top: var(--space-micro);
  margin-bottom: var(--space-one);

  small {
    margin-left: var(--space-normal);
    font-size: var(--font-size-micro);

    span {
      text-transform: uppercase;
    }
  }
}

.bg-image {
  background-image: url("/assets/images/templates/background.jpg");
  width: 100%;
  background-size: cover;
  margin-bottom: var(--space-large);
  border-radius: var(--border-radius-large);
  padding: var(--space-medium);

  .card {
    width: 100%;
    border: 0;

    p {
      font-size: 13px;
    }

    .header {
      p {
        font-weight: 900;
        color: black;
        margin-bottom: var(--space-two);
      }

      .media {
        margin-bottom: var(--space-two);
      }
    }

    .body {
      p {
        white-space: pre-line;
      }
    }

    .footer {
      p {
        margin-top: var(--space-two);
        font-size: 10px !important;
      }
    }

    .template-buttons {
      margin-top: 8px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      max-width: 320px;
      .template-button {
        display: flex;
        padding: 5px 10px;
        align-items: center;
        justify-content: center;
        border: var(--space-micro) solid var(--w-400);
        border-radius: var(--border-radius-normal);
        font-size: var(--font-size-small);
        font-weight: var(--font-weight-bold);
        color: var(--w-400);
        margin-right: var(--space-smaller);
        margin-left: var(--space-smaller);
        margin-bottom: var(--space-smaller);
      }
    }
  }
}
</style>